import { isViewingOtherSessionAtom } from '@/atoms/sessionAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import { useMutateCreateAndLinkPatientProfile } from '@/hooks/mutation/useMutateCreatePatientProfile';
import { useMutateLinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateLinkSessionsToPatientProfile';
import { useMutateUpdateOtherUserSession } from '@/hooks/mutation/useMutateUpdateOtherUserSession';
import { useMutateUpdatePatientDetailsFromSession } from '@/hooks/mutation/useMutateUpdatePatientDetailsFromSession';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { parseCapitalizeString } from '@/lib/utils';
import { toast } from '@heidi/design-system/toast';
import {
  toLegacyPatientProfile,
  useSuggestedPatientMatches,
  type SuggestedMatchCandidates,
  type SuggestedPatientMatch,
} from '@heidi/patients';
import type {
  PatientOutcomeSurface,
  PatientProfileSearchResult,
} from '@heidi/patients/contracts';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import type * as React from 'react';
import { useCallback } from 'react';
import { useFindOrLinkSearch } from './useFindOrLinkSearch';

interface UseSessionListLinkControllerArgs {
  /** Sessions to link (or rename) — `[rowId]` for a single row, the full
   *  selection for the bulk-link bar. */
  selectedSessionIds: string[];
  /** `patient_linked` surface attribution; omitted → the event fires bare. */
  trackingSurface?: PatientOutcomeSurface;
  /** View-shaped candidates. The app adapts the session wire payload before it crosses. */
  patientProfileAppointmentCandidates?: SuggestedMatchCandidates | null;
  /** Opens the legacy full-metadata create modal (empty-text Create). The
   *  parent owns the modal mount so it survives the popover closing. */
  onRequestEmptyCreate: () => void;
  /** Closes the parent Popover/Drawer after a committing action. */
  onClose: () => void;
  /** Read-only sessions disable every action. */
  disabled?: boolean;
}

/**
 * Props the `'popover'`-variant `PatientSearchCombobox` needs to drive the
 * session-list link surface. Same shape the inline header gets from
 * `useFindOrLinkController` — the combobox is identical in both places; only
 * the controller wiring differs.
 */
interface SessionListLinkControllerProps {
  query: string;
  onQueryChange: (next: string) => void;
  onCompositionStart: () => void;
  onCompositionEnd: (e: React.CompositionEvent<HTMLInputElement>) => void;
  results: PatientProfileSearchResult[];
  suggestedMatches: readonly SuggestedPatientMatch[];
  autoLinkCandidate: PatientProfileSearchResult | null;
  isFetching: boolean;
  beIsFresh: boolean;
  isError: boolean;
  disabled: boolean;
  onSelectExisting: (result: PatientProfileSearchResult) => void;
  onCreate: (typedQuery: string) => void;
  onSubmitFreeText: (typedQuery: string) => void;
}

/**
 * List-surface counterpart to `useFindOrLinkController`. Produces the props
 * for `<PatientSearchCombobox variant="popover">` so the session list (row
 * avatar + bulk-link bar) renders the EXACT same selection component as the
 * in-session header.
 *
 * Differences from the header controller:
 * - Acts on `selectedSessionIds[]` (single row AND bulk) rather than the
 *   single current session.
 * - No `committedName` seeding / linked-chip state — the popover only opens
 *   for unlinked sessions and starts fresh on every open.
 * - No funnel analytics (`search_id` / `field_selected` / `time_to_outcome`)
 *   — that instrumentation is a header concern; only the `patient_linked`
 *   `surface` is threaded through.
 * - "Create unlinked session" applies the typed free-text name to EVERY
 *   selected session (single + bulk), routing through the team-member
 *   mutation when an assistant is reviewing on behalf of a clinician —
 *   mirroring `useFindOrLinkMutations.onSubmitFreeText`.
 */
export function useSessionListLinkController({
  selectedSessionIds,
  trackingSurface,
  patientProfileAppointmentCandidates,
  onRequestEmptyCreate,
  onClose,
  disabled = false,
}: UseSessionListLinkControllerArgs): SessionListLinkControllerProps {
  const currentSessionId = useCurrentSessionId();
  // Scope the BE search to the owner of the sessions being acted on. The list
  // filters per-clinician already (assistants see one clinician at a time),
  // so the first selected session is a reliable owner signal — and
  // `currentSessionId` is empty on the list-view route, which would otherwise
  // fall back to the caller's own (empty) profiles for assistants (DEV-12028).
  const sessionIdForSearch = selectedSessionIds[0] || currentSessionId || undefined;

  const search = useFindOrLinkSearch({
    committedName: '',
    enabled: !disabled,
    sessionId: sessionIdForSearch,
  });

  const { data: patientProfiles } = useQueryPatientProfiles();
  const suggestedMatches = useSuggestedPatientMatches(
    patientProfileAppointmentCandidates ?? undefined,
    patientProfiles
  );

  const { mutate: linkSessions } = useMutateLinkSessionsToPatientProfile();
  const { mutate: createAndLink } = useMutateCreateAndLinkPatientProfile();
  const { mutate: updatePatientDetails } = useMutateUpdatePatientDetailsFromSession();
  const { mutate: updatePatientDetailsForOtherUser } = useMutateUpdateOtherUserSession();

  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const teamId = teamDetails?.team.id;

  const onSelectExisting = useCallback(
    (result: PatientProfileSearchResult) => {
      if (disabled) return;
      linkSessions({
        patientProfile: toLegacyPatientProfile(result),
        sessionIds: selectedSessionIds,
        sessionId: sessionIdForSearch,
        ...(trackingSurface && { tracking: { surface: trackingSurface } }),
      });
      onClose();
    },
    [
      disabled,
      linkSessions,
      onClose,
      selectedSessionIds,
      sessionIdForSearch,
      trackingSurface,
    ]
  );

  const onCreate = useCallback(
    (typedQuery: string) => {
      if (disabled) return;
      const trimmed = typedQuery.trim();
      // No typed text → defer to the legacy full-metadata modal.
      if (!trimmed) {
        onRequestEmptyCreate();
        onClose();
        return;
      }
      createAndLink({
        patientProfile: { demographic_string: parseCapitalizeString(trimmed) },
        sessionIds: selectedSessionIds,
        sessionId: sessionIdForSearch,
      });
      onClose();
    },
    [
      disabled,
      createAndLink,
      onClose,
      onRequestEmptyCreate,
      selectedSessionIds,
      sessionIdForSearch,
    ]
  );

  // Mirrors `useFindOrLinkMutations.onSubmitFreeText`'s team-member routing,
  // but loops over `selectedSessionIds` so a bulk selection renames every
  // session. Kept inline (rather than shared) so the single-session header
  // path stays untouched; keep the two routing predicates in sync.
  const onSubmitFreeText = useCallback(
    (typedQuery: string) => {
      if (disabled) return;
      const normalised = parseCapitalizeString(typedQuery.trim());
      if (!normalised || selectedSessionIds.length === 0) return;

      const routeToOtherUser =
        isReviewingOtherUser &&
        !isViewingOtherSession &&
        Boolean(teamId) &&
        Boolean(selectedReviewUser);

      selectedSessionIds.forEach((sessionId) => {
        if (routeToOtherUser && teamId && selectedReviewUser) {
          updatePatientDetailsForOtherUser({
            sessionId,
            teamId,
            memberId: selectedReviewUser,
            input: {
              patient: { name: normalised, demographic_details: normalised },
            },
          });
        } else {
          updatePatientDetails({
            sessionId,
            patientDetails: { name: normalised, demographic_details: normalised },
          });
        }
      });

      toast({
        tone: 'success',
        title:
          selectedSessionIds.length > 1
            ? `Unlinked sessions created: ${normalised}`
            : `Unlinked session created: ${normalised}`,
        duration: 2500,
        // Default toast width is 400px; the message is short, so shrink
        // to content to avoid the empty trailing whitespace.
        className: 'w-auto',
      });
      onClose();
    },
    [
      disabled,
      isViewingOtherSession,
      isReviewingOtherUser,
      onClose,
      selectedReviewUser,
      selectedSessionIds,
      teamId,
      updatePatientDetails,
      updatePatientDetailsForOtherUser,
    ]
  );

  return {
    query: search.query,
    onQueryChange: search.setQuery,
    onCompositionStart: search.onCompositionStart,
    onCompositionEnd: search.onCompositionEnd,
    results: search.results,
    suggestedMatches,
    autoLinkCandidate: search.autoLinkCandidate,
    isFetching: search.isFetching,
    beIsFresh: search.beIsFresh,
    isError: search.isError,
    disabled,
    onSelectExisting,
    onCreate,
    onSubmitFreeText,
  };
}
